import { useCallback, useRef, useEffect } from 'react';
import { useUiStore, LoadingStage } from '../store/uiStore';

/**
 * Master Loading Hook - Single Source of Truth for Loading States
 * 
 * Eliminates competing loading logic by providing a centralized way to manage
 * loading states throughout the application lifecycle.
 * 
 * Loading Stages:
 * - 'css': Initial CSS-based loading (before React mounts)
 * - 'auth': Auth0 authentication processing
 * - 'cache': Cache restoration and validation
 * - 'initial-dashboard': First-time dashboard load (splash screen)
 * - 'dashboard': Normal dashboard navigation (skeleton loaders)
 * - 'complete': Loading finished, app ready
 */
export const useMasterLoading = () => {
  const { masterLoadingState, setMasterLoading } = useUiStore(state => ({
    masterLoadingState: state.masterLoadingState,
    setMasterLoading: state.setMasterLoading,
  }));

  // Debouncing mechanism to prevent rapid state changes
  const lastStageChangeRef = useRef<number>(0);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Progress to a new loading stage with debouncing
   * Only allows forward progression to prevent state conflicts
   * Special handling for logout stage which can be triggered from any state
   */
  const progressToStage = useCallback((
    stage: LoadingStage, 
    message?: string, 
    source: string = 'useMasterLoading'
  ) => {
    const stageOrder: LoadingStage[] = ['css', 'auth', 'cache', 'initial-dashboard', 'dashboard', 'complete'];
    const currentIndex = stageOrder.indexOf(masterLoadingState.stage);
    const newIndex = stageOrder.indexOf(stage);

    // Special case: Allow complete → initial-dashboard for onboarding-to-dashboard first-time experience
    const isOnboardingToDashboard = 
      masterLoadingState.stage === 'complete' && 
      stage === 'initial-dashboard' && 
      (source.includes('Onboarding') || source.includes('onboarding'));

    // Special case: Allow complete → initial-dashboard for route navigation (post-login, PWA first open)
    const isNavigationToInitialDashboard = 
      masterLoadingState.stage === 'complete' && 
      stage === 'initial-dashboard' && 
      (source.includes('Navigation') || source.includes('navigation') || source.includes('post-navigation') || source.includes('Initial'));

    // Allow forward progression, stage completion, logout from any stage, onboarding-to-dashboard reset, or navigation-to-initial-dashboard
    if (newIndex >= currentIndex || stage === 'complete' || stage === 'logout' || isOnboardingToDashboard || isNavigationToInitialDashboard) {
      const isActive = stage !== 'complete';
      const finalMessage = message || getDefaultMessageForStage(stage);
      
      // Prevent rapid changes to the same stage that might cause flicker
      if (masterLoadingState.stage === stage && masterLoadingState.message === finalMessage) {
        console.log(`[useMasterLoading] Skipping duplicate stage change: ${stage} with same message`);
        return;
      }
      
      // Additional check: prevent rapid auth stage cycles on homepage after logout
      if (stage === 'auth' && masterLoadingState.stage === 'complete' && source.includes('Auth0Loading')) {
        const timeSinceLastChange = Date.now() - lastStageChangeRef.current;
        if (timeSinceLastChange < 2000) { // 2 second cooldown for auth cycles
          console.log(`[useMasterLoading] Preventing rapid auth cycle - too soon after last change (${timeSinceLastChange}ms)`);
          return;
        }
      }
      
      // FIX: Skip debouncing for onboarding-to-dashboard, navigation, or initial dashboard loads
      // This eliminates the visible "briefly" flashing states during PWA/Safari loads
      const isInitialDashboardLoad = stage === 'initial-dashboard' && 
        (source.includes('Navigation') || source.includes('Initial') || source.includes('PostLogin'));
      
      if (isOnboardingToDashboard || isNavigationToInitialDashboard || isInitialDashboardLoad) {
        // Clear any pending debounced changes
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
          debounceTimeoutRef.current = null;
        }
        
        lastStageChangeRef.current = Date.now();
        
        const specialCase = isOnboardingToDashboard ? 'onboarding-to-dashboard-reset' : 
                           isInitialDashboardLoad ? 'initial-dashboard-no-debounce' :
                           'navigation-to-dashboard-loading';
        
        console.log(`[useMasterLoading] Progressing (no debounce): ${masterLoadingState.stage} → ${stage}`, {
          source,
          message: finalMessage,
          isActive,
          specialCase
        });

        setMasterLoading(isActive, stage, finalMessage, source);
        return;
      }
      
      // Debouncing: prevent rapid stage changes within 300ms (only for non-critical transitions)
      const now = Date.now();
      const timeSinceLastChange = now - lastStageChangeRef.current;
      
      if (timeSinceLastChange < 300 && stage !== 'complete' && stage !== 'logout') {
        console.log(`[useMasterLoading] Debouncing rapid stage change: ${masterLoadingState.stage} → ${stage}`);
        
        // Clear existing timeout
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
        
        // Set new timeout for the change
        debounceTimeoutRef.current = setTimeout(() => {
          // CRITICAL: Check if already complete before applying debounced change
          // This prevents stage changes from firing after completion
          const currentState = useUiStore.getState().masterLoadingState;
          if (currentState.stage === 'complete' && !currentState.isActive) {
            console.log('[useMasterLoading] Debounced callback cancelled - already complete');
            return;
          }
          
          lastStageChangeRef.current = Date.now();
          console.log(`[useMasterLoading] Debounced progression: ${currentState.stage} → ${stage}`, {
            source,
            message: finalMessage,
            isActive
          });
          setMasterLoading(isActive, stage, finalMessage, source);
        }, 300 - timeSinceLastChange);
        
        return;
      }
      
      // Clear any pending debounced changes
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      
      lastStageChangeRef.current = now;
      
      console.log(`[useMasterLoading] Progressing: ${masterLoadingState.stage} → ${stage}`, {
        source,
        message: finalMessage,
        isActive
      });

      setMasterLoading(isActive, stage, finalMessage, source);
    } else {
      console.warn(`[useMasterLoading] Prevented backward progression: ${masterLoadingState.stage} → ${stage}`);
    }
  }, [masterLoadingState.stage, masterLoadingState.message, setMasterLoading]);

  /**
   * Complete loading and mark as ready with PWA optimizations
   */
  const completeLoading = useCallback((source: string = 'useMasterLoading') => {
    // Prevent duplicate completion calls that might cause flicker
    if (masterLoadingState.stage === 'complete' && !masterLoadingState.isActive) {
      console.log('[useMasterLoading] Skipping duplicate completion call');
      return;
    }
    
    // CRITICAL: Cancel any pending debounced stage changes to prevent stage cycling
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
      console.log('[useMasterLoading] Cancelled pending debounced stage change before completion');
    }
    
    // NEW: Prevent rapid stage cycling ONLY if we're already complete and trying to go back
    // This prevents: complete → dashboard → complete flicker
    // But allows: initial-dashboard → complete (normal completion flow)
    const timeSinceLastChange = Date.now() - lastStageChangeRef.current;
    const wasRecentlyCompleted = masterLoadingState.stage === 'complete' && timeSinceLastChange < 500;
    
    if (wasRecentlyCompleted) {
      console.warn('[useMasterLoading] Blocked rapid re-completion - preventing complete→loading→complete flicker', {
        timeSinceLastChange,
        currentStage: masterLoadingState.stage,
        source
      });
      return;
    }
    
    // PWA-specific completion handling
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isPWA || isIOSSafari) {
      console.log('[useMasterLoading] PWA-optimized completion flow', { 
        source, 
        isPWA, 
        isIOSSafari 
      });
    }
    
    console.log('[useMasterLoading] Completing loading flow', { source });
    setMasterLoading(false, 'complete', null, source);
  }, [masterLoadingState.stage, masterLoadingState.isActive, setMasterLoading]);

  /**
   * Determine if BlendingSpinner should show with PWA awareness
   * Only show for auth, cache, and dashboard stages
   * Consider PWA environment for optimized loading experience
   * 
   * CRITICAL: Also waits for feature flags to load to prevent Sidebar skeleton flashing
   */
  const shouldShowBlendingSpinner = useCallback(() => {
    // Get PWA environment info
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    // Check cache quality
    let hasMeaningfulCache = false;
    let cacheAge = 0;
    try {
      const cacheInfo = localStorage.getItem('td_cache_quality');
      if (cacheInfo) {
        const parsed = JSON.parse(cacheInfo);
        hasMeaningfulCache = parsed.hasMeaningfulCache === true;
        cacheAge = Date.now() - (parsed.restoredAt || 0);
      }
    } catch (e) {
      // Ignore cache check errors
    }
    
    // Enhanced PWA optimization logic - only skip spinner under very specific conditions
    if ((isPWA || isIOSSafari) && hasMeaningfulCache) {
      // Only skip spinner if ALL of these conditions are met:
      // 1. Cache is fresh (less than 5 minutes old)
      // 2. High memory pressure detected
      // 3. User is on dashboard stage specifically
      const isCacheFresh = cacheAge < 5 * 60 * 1000; // 5 minutes
      const isDashboardStage = masterLoadingState.stage === 'dashboard';
      
      if (isCacheFresh && isDashboardStage) {
        try {
          // @ts-ignore - Check memory pressure
          if (window.performance && window.performance.memory) {
            // @ts-ignore
            const { usedJSHeapSize, totalJSHeapSize } = window.performance.memory;
            if (totalJSHeapSize > 0) {
              const memoryUsage = usedJSHeapSize / totalJSHeapSize;
              if (memoryUsage > 0.85) { // Increased threshold to 85%
                console.log('[useMasterLoading] Skipping BlendingSpinner due to high memory pressure + fresh cache + dashboard stage');
                return false;
              }
            }
          }
        } catch (e) {
          // Memory API not available - don't skip spinner
        }
      }
      
      // For PWA/iOS Safari, always show spinner during auth stages for better UX
      if (masterLoadingState.stage === 'auth' || masterLoadingState.stage === 'cache') {
        console.log('[useMasterLoading] PWA/iOS Safari: Showing BlendingSpinner for auth/cache stage');
      }
    }
    
    // Show BlendingSpinner for splash screen scenarios (initial-dashboard)
    // Hide for regular navigation (dashboard) to use skeleton loaders instead
    const shouldShow = masterLoadingState.isActive && 
                      masterLoadingState.stage !== 'css' && 
                      masterLoadingState.stage !== 'complete' &&
                      masterLoadingState.stage !== 'dashboard'; // Regular nav uses skeletons
                      // initial-dashboard WILL show BlendingSpinner ✅
    
    if (shouldShow) {
      console.log('[useMasterLoading] BlendingSpinner should show:', {
        stage: masterLoadingState.stage,
        message: masterLoadingState.message,
        source: masterLoadingState.source,
        isPWA,
        isIOSSafari,
        hasMeaningfulCache
      });
    }
    
    return shouldShow;
  }, [masterLoadingState]);

  /**
   * Check if specific stage is active
   */
  const isStageActive = useCallback((stage: LoadingStage) => {
    return masterLoadingState.isActive && masterLoadingState.stage === stage;
  }, [masterLoadingState]);

  /**
   * Check if loading is complete
   */
  const isComplete = useCallback(() => {
    return masterLoadingState.stage === 'complete' && !masterLoadingState.isActive;
  }, [masterLoadingState]);

  /**
   * Force reset to a specific stage (use sparingly)
   */
  const resetToStage = useCallback((
    stage: LoadingStage, 
    message?: string, 
    source: string = 'useMasterLoading-reset'
  ) => {
    console.warn('[useMasterLoading] Force resetting to stage:', stage, { source, message });
    const isActive = stage !== 'complete';
    const finalMessage = message || getDefaultMessageForStage(stage);
    setMasterLoading(isActive, stage, finalMessage, source);
  }, [setMasterLoading]);

  /**
   * Reset loading state for logout/restart scenarios
   */
  const resetForLogout = useCallback((source: string = 'useMasterLoading-logout') => {
    console.log('[useMasterLoading] Starting logout process', { source });
    setMasterLoading(true, 'logout', 'Logging out...', source);
  }, [setMasterLoading]);

  // Cleanup effect to clear debounce timeouts on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
    };
  }, []);

  return {
    // State
    masterLoadingState,
    
    // Actions
    progressToStage,
    completeLoading,
    resetToStage,
    resetForLogout,
    
    // Utilities
    shouldShowBlendingSpinner,
    isStageActive,
    isComplete,
    
    // Convenience checks
    isAuthStage: isStageActive('auth'),
    isCacheStage: isStageActive('cache'),
    isDashboardStage: isStageActive('dashboard'),
  };
};

/**
 * Get default message for a loading stage
 */
function getDefaultMessageForStage(stage: LoadingStage): string {
  switch (stage) {
    case 'css':
      return 'Starting application...';
    case 'auth':
      return 'Authenticating...';
    case 'cache':
      return 'Restoring data...';
    case 'initial-dashboard':
      return 'Loading dashboard...';
    case 'dashboard':
      return 'Refreshing data...';
    case 'logout':
      return 'Logging out...';
    case 'complete':
      return '';
    default:
      return 'Loading...';
  }
}

/**
 * Hook for PWA-specific loading optimization
 */
export const usePWADetection = () => {
  const isPWA = window.matchMedia('(display-mode: standalone)').matches;
  const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isMobile = isPWA || isIOSSafari;

  return { isPWA, isIOSSafari, isMobile };
};
